import React, { useState } from "react";
import { Table, Button, Row, Col, Pagination } from "react-bootstrap";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
    PaginationState,
} from "@tanstack/react-table";
import { WEB_BASE_URL } from "../../config";
import { getRankImage } from "../../utils/getRankImage";
import { getRankColor } from "../../utils/getRankColor";
import { hasRank } from "../../utils/hasRank";
import { apiClient } from "../../services/api";

interface ExportResultTableProps {
    teamMembers: ExportNetworkUser[];
    isLoading?: boolean;
}
export interface ExportNetworkUser {
    id: string;
    level: number;
    name: string;
    email: string;
    multyhealth_code: string;
    nexos: boolean;
    cellphone: string;
    photo: string;
    loyalty_status: boolean;
    /** Puntos de red del mes en curso (caché UserPointsCache de settlement-ms). */
    loyalty_points?: number;
    /** Puntos de las compras propias del mes; no cuentan para la red. */
    own_points?: number;
    /** Puntos de bonificación del mes: los generan las compras de sus directos. */
    influencer_points?: number;

    last_rank?: {
        period: {
            start: string;
            end: string;
        };
        calculation_date: string;
        total_points: number;
        rank: {
            name: string;
            points_required: number;
            qualifies: boolean;
            active_level1_users: number;
            required_active_users: number;
        };
    };
}

const ExportResultTable: React.FC<ExportResultTableProps> = ({
    teamMembers = [],
    isLoading = false,
}) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const [downloadingUser, setDownloadingUser] = useState<string | null>(null);

    const copyToClipboard = (code: string) => {
        const referralLink = `${WEB_BASE_URL}/sign-up?code=${code}`;
        navigator.clipboard.writeText(referralLink);
        alert("¡Enlace copiado al portapapeles!");
    };

    const handleDownload = async (userId: string) => {
        try {
            setDownloadingUser(userId);

            const response = await apiClient.get(
                `/users/network/tree?user_id=${userId}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: localStorage.getItem("token") || "",
                    },
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement("a");
            link.href = url;
            link.download = "network.xlsx";

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } finally {
            setDownloadingUser(null);
        }
    };



    const columns: ColumnDef<ExportNetworkUser>[] = [
        {
            accessorKey: "multyhealth_code",
            header: "Código de distribuidor",
            cell: ({ getValue }) => (
                <div className="d-flex align-items-center">
                    <span className="me-2">{String(getValue() ?? "")}</span>

                    <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => copyToClipboard(String(getValue() ?? ""))}
                    >
                        <i className="ri-file-copy-line"></i>
                    </Button>
                </div>
            ),
        },
        {
            accessorKey: "name",
            header: "Nombre",
            cell: ({ getValue }) => (
                <div className="d-flex align-items-center">
                    <span className="me-2">{String(getValue() ?? "")}</span>
                </div>
            ),
        },
        {
            accessorKey: "sponsor",
            header: "Auspiciador",
            cell: ({ getValue }) => {
                const sponsor = getValue() as {
                    name?: string;
                    email?: string;
                } | null;

                if (!sponsor) {
                    return <span>-</span>;
                }

                return (
                    <div className="d-flex flex-column">
                        <span>{sponsor.name || "-"}</span>
                        <small className="text-muted">
                            {sponsor.email || "-"}
                        </small>
                    </div>
                );
            },
        },
        {
            accessorKey: "level",
            header: "Nivel",
            cell: ({ getValue }) => (
                <div className="d-flex align-items-center">
                    <span className="me-2">{String(getValue() ?? "")}</span>
                </div>
            ),
        },

        {
            accessorKey: "loyalty_status",
            header: "Estado",
            cell: ({ getValue }) => {
                const active = Boolean(getValue());

                return (
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 72,
                            height: 32,
                            padding: "0 12px",
                            borderRadius: 6,
                            background: "#111418",
                            border: `1px solid ${active ? "#2dd36f" : "#ff3b30"}`,
                            color: active ? "#2dd36f" : "#ff3b30",
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {active ? "Activo" : "Inactivo"}
                    </div>
                );
            },
        },

        {
            // Los puntos del mes salen de la caché de puntos de red, no de
            // `last_rank.total_points`: ese es el snapshot del último corte de
            // rangos, o sea el período ANTERIOR, y por eso la tabla mostraba
            // puntos viejos a usuarios sin órdenes este mes (el Excel de
            // /users/network/report ya usaba `loyalty_points`).
            accessorFn: (row) => row.loyalty_points ?? 0,
            id: "royalties",
            header: "Puntos de regalías",
            cell: ({ getValue }) => Number(getValue()).toLocaleString(),
        },

        {
            accessorFn: (row) => row.influencer_points ?? 0,
            id: "bonus",
            header: "Puntos de bonificaciones",
            cell: ({ getValue }) => Number(getValue()).toLocaleString(),
        },

        {
            accessorFn: (row) => row.last_rank?.rank?.name ?? "Sin rango",
            id: "rank",
            header: "Rango actual",
            cell: ({ getValue }) => {
                const rank = String(getValue());

                if (!hasRank(rank)) {
                    return <>Sin rango</>;
                }

                return (
                    <div className="d-flex align-items-center gap-2">
                        <img
                            src={getRankImage(rank)}
                            alt={rank}
                            style={{
                                width: 20,
                                height: 20,
                                objectFit: "contain",
                                filter: `drop-shadow(0 0 5px ${getRankColor(rank)})`,
                            }}
                        />

                        <span>{rank}</span>
                    </div>
                );
            },
        },

        {
            accessorFn: (row) => row.last_rank?.rank?.active_level1_users ?? 0,
            id: "registered",
            header: "Usuarios registrados",
            cell: ({ getValue }) => getValue(),
        },

        {
            accessorFn: (row) => row.last_rank?.rank?.required_active_users ?? 0,
            id: "active",
            header: "Usuarios activos",
            cell: ({ getValue }) => getValue(),
        },
        {
            accessorKey: "id",
            header: "Red",
            enableSorting: false,
            cell: ({ getValue, row }) => {
                const userId = String(getValue());

                return (
                    <Button
                        variant="success"
                        size="sm"
                        className="d-flex align-items-center gap-2"
                        disabled={downloadingUser === userId}
                        title={`Descargar la red de ${row.original.name}`}
                        onClick={() => handleDownload(userId)}
                    >
                        {downloadingUser === userId ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span>Generando...</span>
                            </>
                        ) : (
                            <>
                                <i className="ri-download-2-line"></i>
                                <span>Descargar</span>
                            </>
                        )}
                    </Button>
                );
            },
        },
    ];
    // Create table instance
    const table = useReactTable({
        data: teamMembers || [],
        columns,
        state: {
            sorting,
            globalFilter,
            pagination,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: false,
    });

    if (isLoading) {
        return (
            <div className="table-responsive mt-3">
                <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Cargando usuarios...</span>
                    </div>
                    <p className="mt-2 mb-0">Cargando usuarios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3">

            {/* Table */}
            <div className="table-responsive">
                <Table className="align-middle">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className={
                                            header.column.getCanSort()
                                                ? "cursor-pointer user-select-none"
                                                : ""
                                        }
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div className="d-flex align-items-center">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}
                                            {header.column.getCanSort() && (
                                                <span className="ms-1">
                                                    {{
                                                        asc: <i className="ri-arrow-up-s-line"></i>,
                                                        desc: <i className="ri-arrow-down-s-line"></i>,
                                                    }[header.column.getIsSorted() as string] ?? (
                                                            <i className="ri-expand-up-down-line text-muted"></i>
                                                        )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-4">
                                    {globalFilter
                                        ? "No se encontraron usuarios que coincidan con la búsqueda."
                                        : "No hay usuarios en la red de este usuario."}
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {table.getPageCount() > 1 && (
                <Row className="mt-3">
                    <Col md={6} className="d-flex align-items-center">
                        <span className="text-muted">
                            Mostrando{" "}
                            {table.getState().pagination.pageIndex *
                                table.getState().pagination.pageSize +
                                1}{" "}
                            a{" "}
                            {Math.min(
                                (table.getState().pagination.pageIndex + 1) *
                                table.getState().pagination.pageSize,
                                table.getFilteredRowModel().rows.length,
                            )}{" "}
                            de {table.getFilteredRowModel().rows.length} registros
                        </span>
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                        <Pagination className="mb-0">
                            <Pagination.First
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}
                            />
                            <Pagination.Prev
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            />

                            {/* Page numbers */}
                            {Array.from(
                                { length: Math.min(5, table.getPageCount()) },
                                (_, i) => {
                                    const pageIndex = table.getState().pagination.pageIndex;
                                    const totalPages = table.getPageCount();
                                    let startPage = Math.max(0, pageIndex - 2);
                                    let endPage = Math.min(totalPages - 1, startPage + 4);

                                    if (endPage - startPage < 4) {
                                        startPage = Math.max(0, endPage - 4);
                                    }

                                    const currentPage = startPage + i;
                                    if (currentPage <= endPage) {
                                        return (
                                            <Pagination.Item
                                                key={currentPage}
                                                active={currentPage === pageIndex}
                                                onClick={() => table.setPageIndex(currentPage)}
                                            >
                                                {currentPage + 1}
                                            </Pagination.Item>
                                        );
                                    }
                                    return null;
                                },
                            )}

                            <Pagination.Next
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            />
                            <Pagination.Last
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}
                            />
                        </Pagination>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default ExportResultTable;
